/**
 * RebalanceModal - Modal de simulacao e execucao de balanceamento de carga
 *
 * Exibe: movimentacoes propostas, impacto antes/depois, operadores envolvidos.
 * Permite aprovar ou cancelar.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Check,
  Loader2,
  Shuffle,
  TrendingDown,
  X,
} from 'lucide-react';
import type { RebalancePlan, RebalanceMove } from '../services/rebalanceService';
import { WORK_SOURCE_COLORS, WORK_SOURCE_LABELS } from '../types/workItem';

// ── Impact delta arrow ───────────────────────────────────────

function Delta({ before, after, unit, invert }: { before: number; after: number; unit?: string; invert?: boolean }) {
  const diff = after - before;
  const improved = invert ? diff > 0 : diff < 0;
  const neutral = diff === 0;
  const color = neutral ? 'text-slate-400' : improved ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';
  const sign = diff > 0 ? '+' : '';
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] text-slate-500 dark:text-slate-400 tabular-nums">{before}{unit}</span>
      <ArrowRight size={10} className="text-slate-400" />
      <span className={`text-[10px] font-semibold tabular-nums ${color}`}>{after}{unit}</span>
      {!neutral && <span className={`text-[9px] font-mono ${color}`}>({sign}{diff}{unit})</span>}
    </div>
  );
}

// ── Props ────────────────────────────────────────────────────

interface RebalanceModalProps {
  plan: RebalancePlan;
  isExecuting: boolean;
  onExecute: () => void;
  onClose: () => void;
}

export const RebalanceModal: React.FC<RebalanceModalProps> = ({
  plan,
  isExecuting,
  onExecute,
  onClose,
}) => {
  const { moves, impact } = plan;
  const hasNoMoves = moves.length === 0;

  // Group moves by operator involved
  const involvedOperators = useMemo(() => {
    const ops = new Set<string>();
    for (const m of moves) {
      ops.add(m.fromUserName);
      ops.add(m.toUserName);
    }
    return [...ops].sort();
  }, [moves]);

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-[680px] max-h-[85vh] rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
          <div className="flex items-center gap-2.5">
            <Shuffle size={16} className="text-blue-600 dark:text-blue-400" />
            <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-200">Rebalanceamento de Carga</h2>
            <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[9px] font-bold text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
              {moves.length} movimentacao{moves.length !== 1 ? 'es' : ''}
            </span>
          </div>
          <button onClick={onClose} disabled={isExecuting} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700">
            <X size={16} className="text-slate-400" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

          {/* No moves */}
          {hasNoMoves && (
            <div className="flex flex-col items-center py-8 gap-2">
              <Check size={32} className="text-emerald-500" />
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Carga ja esta equilibrada</p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">Nenhuma movimentacao necessaria.</p>
            </div>
          )}

          {/* Impact section */}
          {!hasNoMoves && (
            <>
              <section>
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2">
                  Impacto Estimado
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded border border-slate-200 dark:border-slate-700 px-3 py-2.5 bg-slate-50/50 dark:bg-slate-900/20">
                    <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Carga Max</span>
                    <Delta before={impact.maxLoadBefore} after={impact.maxLoadAfter} />
                  </div>
                  <div className="rounded border border-slate-200 dark:border-slate-700 px-3 py-2.5 bg-slate-50/50 dark:bg-slate-900/20">
                    <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">SLA Violado</span>
                    <Delta before={impact.slaViolatedBefore} after={impact.slaViolatedAfter} unit="%" />
                  </div>
                  <div className="rounded border border-slate-200 dark:border-slate-700 px-3 py-2.5 bg-slate-50/50 dark:bg-slate-900/20">
                    <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Gargalo Total</span>
                    <Delta before={impact.bottleneckBefore} after={impact.bottleneckAfter} />
                  </div>
                </div>
              </section>

              {/* Operators involved */}
              <section>
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2">
                  Operadores Envolvidos ({involvedOperators.length})
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {involvedOperators.map(name => (
                    <span key={name} className="rounded bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-[10px] font-medium text-slate-700 dark:text-slate-300">
                      {name}
                    </span>
                  ))}
                </div>
              </section>

              {/* Moves table */}
              <section>
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2">
                  Movimentacoes
                </h3>
                <div className="space-y-1 max-h-[260px] overflow-y-auto">
                  {moves.map((move, idx) => (
                    <div
                      key={move.item.id}
                      className="flex items-center gap-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 px-3 py-2 text-[11px]"
                    >
                      <span className="shrink-0 text-[9px] font-mono text-slate-400 w-4 text-right">{idx + 1}</span>
                      <span className={`shrink-0 rounded px-1 py-0.5 text-[8px] font-semibold ${WORK_SOURCE_COLORS[move.item.source]}`}>
                        {WORK_SOURCE_LABELS[move.item.source].slice(0, 3)}
                      </span>
                      <span className="min-w-0 flex-1 truncate font-medium text-slate-700 dark:text-slate-300">{move.item.title}</span>
                      <span className="shrink-0 text-[10px] font-semibold text-red-600 dark:text-red-400 truncate max-w-[80px]">{move.fromUserName}</span>
                      <ArrowRight size={10} className="shrink-0 text-slate-400" />
                      <span className="shrink-0 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 truncate max-w-[80px]">{move.toUserName}</span>
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
          <div className="flex items-center gap-1.5 text-[10px] text-slate-500 dark:text-slate-400">
            <AlertTriangle size={12} />
            <span>Alteracoes serao aplicadas imediatamente</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={isExecuting}
              className="rounded border border-slate-300 dark:border-slate-600 px-3 py-1.5 text-[11px] font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
            >
              Cancelar
            </button>
            {!hasNoMoves && (
              <button
                onClick={onExecute}
                disabled={isExecuting}
                className="flex items-center gap-1.5 rounded bg-blue-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {isExecuting ? (
                  <>
                    <Loader2 size={12} className="animate-spin" />
                    Aplicando...
                  </>
                ) : (
                  <>
                    <Check size={12} />
                    Aplicar {moves.length} movimentacao{moves.length !== 1 ? 'es' : ''}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
